import { SAVED_VIEWS, savedViewsListKey } from '../actions/savedViews';

// ENG-3551: one list per user/org/page. Mutations are applied on success only (the
// hook owns pending/saving UI), and every successful mutation retires the list's
// in-flight fetch so a slower, older list reply cannot resurrect a deleted view or
// roll back an update.
export const initialSavedViewsState = { lists: {} };

export const EMPTY_SAVED_VIEWS_LIST = Object.freeze({
  views: Object.freeze([]),
  loaded: false,
  loading: false,
  error: null,
  requestId: null,
});

const hasContext = (meta) => Boolean(meta && meta.page && meta.orgId != null && meta.userId != null);

const withList = (state, meta, update) => {
  const key = savedViewsListKey(meta);
  const current = state.lists[key] || EMPTY_SAVED_VIEWS_LIST;
  return { ...state, lists: { ...state.lists, [key]: { ...current, ...update(current) } } };
};

const responseView = (action) => {
  const view = action.payload?.data?.saved_inbox_view;
  return view && view.id != null ? view : null;
};

const failureStatus = (action) => action.payload?.response?.status ?? null;

// eslint-disable-next-line default-param-last
const savedViews = (state = initialSavedViewsState, action) => {
  if (!action || !hasContext(action.meta)) return state;
  const { meta } = action;

  switch (action.type) {
    case SAVED_VIEWS.FETCH_BEGIN:
      return withList(state, meta, () => ({ loading: true, error: null, requestId: meta.requestId }));
    case SAVED_VIEWS.FETCH_SUCCESS: {
      const current = state.lists[savedViewsListKey(meta)];
      if (!current || current.requestId !== meta.requestId) return state;
      const views = action.payload?.data?.saved_inbox_views;
      if (!Array.isArray(views)) {
        return withList(state, meta, () => ({ loading: false, error: { status: null }, requestId: null }));
      }
      return withList(state, meta, () => ({
        views, loaded: true, loading: false, error: null, requestId: null,
      }));
    }
    case SAVED_VIEWS.FETCH_FAIL: {
      const current = state.lists[savedViewsListKey(meta)];
      if (!current || current.requestId !== meta.requestId) return state;
      return withList(state, meta, () => ({
        loading: false, error: { status: failureStatus(action) }, requestId: null,
      }));
    }
    case SAVED_VIEWS.CREATE_SUCCESS: {
      const view = responseView(action);
      if (!view) return state;
      return withList(state, meta, (current) => ({
        views: [...current.views.filter((item) => item.id !== view.id), view],
        loading: false,
        requestId: null,
      }));
    }
    case SAVED_VIEWS.UPDATE_SUCCESS: {
      const view = responseView(action);
      if (!view) return state;
      return withList(state, meta, (current) => ({
        views: current.views.map((item) => (item.id === view.id ? view : item)),
        loading: false,
        requestId: null,
      }));
    }
    case SAVED_VIEWS.DELETE_SUCCESS:
      return withList(state, meta, (current) => ({
        views: current.views.filter((item) => String(item.id) !== String(meta.id)),
        loading: false,
        requestId: null,
      }));
    default:
      return state;
  }
};

export const selectSavedViewsList = (state, context) => {
  if (!context || !state?.savedViews) return EMPTY_SAVED_VIEWS_LIST;
  return state.savedViews.lists[savedViewsListKey(context)] || EMPTY_SAVED_VIEWS_LIST;
};

export default savedViews;
